<template>
  <div class="auth-container">
    <div class="auth-card">
      <!-- Logo -->
      <div class="logo-container">
        <router-link to="/" class="logo-main">
          <img src="/logo-no-bg.png" alt="BlogDavid Logo" class="logo-image" />
        </router-link>
      </div>

      <!-- Header -->
      <div class="auth-header">
        <h1 class="auth-title">Esqueci minha senha</h1>
        <p class="auth-subtitle">
          Informe seu email e enviaremos um link para redefinir sua senha
        </p>
      </div>

      <!-- Success state -->
      <div v-if="sent" class="success-box">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>
          Se este email estiver cadastrado, você receberá um link em breve.
          Verifique também a pasta de spam.
        </p>
      </div>

      <!-- Form -->
      <form v-else class="auth-form" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="email" class="form-label">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
            Email
          </label>
          <input
            id="email"
            v-model="email"
            type="email"
            required
            class="form-input"
            placeholder="seu@email.com"
            :disabled="authStore.isLoading"
            autocomplete="email"
          />
        </div>

        <div v-if="errorMsg" class="error-box">{{ errorMsg }}</div>

        <button type="submit" :disabled="authStore.isLoading" class="btn-primary">
          <span v-if="authStore.isLoading" class="btn-loading">
            <div class="spinner"></div>
            Enviando...
          </span>
          <span v-else class="btn-content">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Enviar link
          </span>
        </button>

        <p class="auth-footer">
          Lembrou a senha?
          <router-link to="/login" class="auth-link">Voltar para o login</router-link>
        </p>
      </form>

      <!-- Link de voltar após sucesso -->
      <p v-if="sent" class="auth-footer" style="margin-top: 1.5rem; text-align: center;">
        <router-link to="/login" class="auth-link">Voltar para o login</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useAuthStore } from "@/store/auth";

export default {
  name: "ForgotPassword",
  setup() {
    const authStore = useAuthStore();
    const email = ref("");
    const sent = ref(false);
    const errorMsg = ref("");

    const handleSubmit = async () => {
      errorMsg.value = "";
      const result = await authStore.forgotPassword(email.value);
      if (result.success) {
        sent.value = true;
      } else {
        errorMsg.value = result.error || "Erro ao processar solicitação.";
      }
    };

    return { authStore, email, sent, errorMsg, handleSubmit };
  },
};
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  padding: 2rem 1rem;
}

.auth-card {
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 3rem 2.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.logo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-main {
  display: inline-block;
  transition: transform 0.2s;
}

.logo-main:hover {
  transform: scale(1.05);
}

.logo-image {
  height: 60px;
  width: auto;
  object-fit: contain;
}

.auth-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.auth-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.auth-subtitle {
  font-size: 1rem;
  color: #64748b;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a202c;
}

.form-label svg {
  width: 18px;
  height: 18px;
  color: #3b82f6;
}

.form-input {
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  color: #1a202c;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: all 0.2s;
}

.form-input:focus {
  background: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-input::placeholder {
  color: #94a3b8;
}

.btn-primary {
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-content svg {
  width: 20px;
  height: 20px;
}

.btn-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.auth-footer {
  text-align: center;
  font-size: 0.875rem;
  color: #64748b;
}

.auth-link {
  color: #3b82f6;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}

.auth-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.success-box {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1.25rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #166534;
  font-size: 0.9375rem;
  line-height: 1.6;
}

.success-box svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: #16a34a;
  margin-top: 2px;
}

.error-box {
  padding: 0.875rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 0.875rem;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 2rem 1.5rem;
  }

  .auth-title {
    font-size: 1.5rem;
  }
}
</style>
